import { useEffect, useRef, useState } from "react"
import { ArrowLeft } from "lucide-react"
import { toast } from 'sonner'
import { useUserRol, useTienePermiso} from '../App'
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService'
import { RechazoModal } from "./RechazoModal"
import { Button } from "./ui/button"
import { Card, CardContent } from "./ui/card"
import { Input } from "./ui/input"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"

interface FormData105B02 {
  ROW_ID?: string
  numero_solicitud: string
  fecha: string
  ciclo_exposicion: string
  temp_estandar_negro: string
  temp_panel_negro: string
  humedad_relativa: string
  irradiacion: string
  longitud_onda: string
  tiempo_exposicion: string
  exposicion_radiante: string
  grado_escala_gris: string
  observaciones: string
  mostrarObservaciones: boolean
  desv_metodo: string
  realizo: string
  autorizo: string
}

interface Props {
  mode: "create" | "view" | "edit"
  initialData?: any
  onSave?: (data: FormData105B02) => void
  onCancel?: () => void
  realizoUser?: string
  onRefreshBackend?: () => void
  rol?: string
  pedidoRowId?: string
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

const DEFAULTS: FormData105B02 = {
  numero_solicitud: '', fecha: '',
  ciclo_exposicion: '', temp_estandar_negro: '', temp_panel_negro: '',
  humedad_relativa: '', irradiacion: '', longitud_onda: '',
  tiempo_exposicion: '', exposicion_radiante: '', grado_escala_gris: '',
  observaciones: '', mostrarObservaciones: false, desv_metodo: '', realizo: '', autorizo: '',
}

const TD = "border border-gray-500 px-1 py-0.5 align-top text-xs"

export function NMXA105B02Form({ mode, initialData = {}, onSave, onCancel, realizoUser, onRefreshBackend, rol, pedidoRowId, onAutosave }: Props) {
  const globalRol = useUserRol()
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const effectiveRol = rol || globalRol
  const [formData, setFormData] = useState<FormData105B02>({
    ...DEFAULTS,
    realizo: mode === 'create' && realizoUser ? realizoUser : '',
    ...initialData,
    mostrarObservaciones: initialData?.MOSTRAR_OBSERVACIONES === true,
  })
  const [editMode, setEditMode] = useState(false)
  const [showRechazoModal, setShowRechazoModal] = useState(false)
  const [rechazoLoading, setRechazoLoading] = useState(false)

  const initialDataSyncedRef = useRef(false)
  useEffect(() => {
    if (initialDataSyncedRef.current) return
    initialDataSyncedRef.current = true
    setFormData({ ...DEFAULTS, realizo: realizoUser || '', ...initialData, mostrarObservaciones: initialData?.MOSTRAR_OBSERVACIONES === true })
    setEditMode(false)
  }, [initialData])

  const aceptadoRaw = initialData?.ACEPTADO ?? initialData?.aceptado
  const aceptadoValue = typeof aceptadoRaw === 'string' ? aceptadoRaw.toLowerCase().trim() : ''
  const isAceptado = aceptadoValue === 'aceptado' || aceptadoValue === 'rechazado'
  const isReadOnly = mode === 'view' && !editMode
  const [autosaveRowId, setAutosaveRowId] = useState<string>(formData?.ROW_ID || initialData?.ROW_ID || '')
  const [autosaveNomId, setAutosaveNomId] = useState<string>(initialData?.nomId || '')
  const rowId = autosaveRowId
  const nomId = autosaveNomId

  const set = (field: keyof FormData105B02, value: any) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value }
      const irr = parseFloat(field === 'irradiacion' ? value : next.irradiacion)
      const tiempo = parseFloat(field === 'tiempo_exposicion' ? value : next.tiempo_exposicion)
      if (!isNaN(irr) && !isNaN(tiempo)) {
        next.exposicion_radiante = (Math.round(irr * 3.6 * tiempo * 10 + Number.EPSILON) / 10).toFixed(1)
      }
      return next
    })
  }

  const formPayload = () => {
    const { ROW_ID, ...rest } = formData
    return rest
  }

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      if (onSave) onSave(formPayload())
    } finally {
      setGuardando(false)
    }
  }

  const handleGuardarEdicion = () => {
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de NMX-A-105-B02? Esta acción no se puede deshacer.')) return
    if (onSave) onSave({ ...formPayload(), ROW_ID: rowId, _EDIT_MODE: true } as any)
  }

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'NMX-A-105-B02-INNTEX-2019 DETERMINACIÓN DE LA SOLIDEZ DEL COLOR A LA LUZ ARTIFICIAL: ENSAYO DE LA LÁMPARA DE DECOLORACIÓN DE ARCO DE XENÓN',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onCancel?.()
  }

  const handleAccion = async () => {
    if (!window.confirm('Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba NMX-A-105-B02?')) return
    try {
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, 'aceptado', undefined, undefined, nomId || undefined)
      toast.success('Prueba aceptada correctamente.')
      onRefreshBackend?.()
    } catch (e: any) { toast.error(e.message) }
  }

  const inputCls = (extra?: string) =>
    `h-6 text-xs px-1 ${isReadOnly ? 'bg-transparent border-0 shadow-none cursor-not-allowed' : ''} ${extra ?? ''}`

  return (
    <>
      {onCancel && (
        <Button variant="ghost" size="sm" onClick={handleBackFlush}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
      )}
      <Card className="w-full max-w-4xl mx-auto">
      <CardContent className="p-4 space-y-3">

        {/* ── HEADER TABLE ── */}
        <table className="w-full text-xs border-collapse border-2 border-gray-700">
          <tbody>
            {/* Row 1: Número de solicitud + Fecha */}
            <tr>
              <td colSpan={3} className={TD}>
                <div className="flex items-center gap-2">
                  <span className="font-bold whitespace-nowrap">NUMERO DE SOLICITUD:</span>
                  <Input className={inputCls('flex-1')}
                    value={formData.numero_solicitud} readOnly={isReadOnly}
                    onChange={e => set('numero_solicitud', e.target.value)} />
                </div>
              </td>
              <td className={TD}>
                <div className="flex items-center gap-2">
                  <span className="font-bold whitespace-nowrap">FECHA:</span>
                  <Input type="date" className={inputCls('flex-1')}
                    value={formData.fecha} readOnly={isReadOnly}
                    onChange={e => set('fecha', e.target.value)} />
                </div>
              </td>
            </tr>
            {/* Row 2: Title + MET */}
            <tr>
              <td colSpan={3} className={`${TD} text-center font-bold py-1`}>
                NMX-A-105-B02-INNTEX-2019 DETERMINACIÓN DE LA SOLIDEZ DEL COLOR A LA LUZ<br />
                ARTIFICIAL: ENSAYO DE LA LÁMPARA DE DECOLORACIÓN DE ARCO DE XENÓN
              </td>
              <td className={`${TD} text-center font-bold whitespace-nowrap`}>MET-7.2-23</td>
            </tr>
          </tbody>
        </table>

        {/* ── MAIN SECTION: contenido (izq) + Realizó/Autorizó (der) ── */}
        <table className="w-full text-xs border-collapse border border-gray-500">
          <tbody>
            <tr>
              {/* Left: form fields */}
              <td className="border border-gray-500 p-2 align-top">
                {/* Fixed values row */}
                <div className="text-xs mb-2 space-y-0.5">
                  <div><span>Equipo: </span><span className="underline">Enfriado por agua</span></div>
                  <div><span>Método: </span><span className="underline">5</span></div>
                  <div><span>Modo oscilante: </span><span className="underline">NO</span></div>
                  <div><span>Iluminante (evaluación): </span><span className="underline">D65</span></div>
                </div>

                {/* Condiciones de exposición */}
                <div className="text-xs mb-1">Condiciones de exposición:</div>
                <table className="w-full text-xs border-collapse border border-gray-400 mb-2">
                  <tbody>
                    <tr>
                      <td className="border border-gray-400 px-1 py-0.5 w-[35%]">Ciclo de exposición:</td>
                      <td className="border border-gray-400 p-0.5 w-[15%]">
                        <Input className={inputCls('w-full')}
                          value={formData.ciclo_exposicion} readOnly={isReadOnly}
                          onChange={e => set('ciclo_exposicion', e.target.value)} />
                      </td>
                      <td className="border border-gray-400 px-1 py-0.5 w-[35%]">Temperatura del estándar negro (°C):</td>
                      <td className="border border-gray-400 p-0.5 w-[15%]">
                        <Input className={inputCls('w-full')}
                          value={formData.temp_estandar_negro} readOnly={isReadOnly}
                          onChange={e => set('temp_estandar_negro', e.target.value)} />
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-400 px-1 py-0.5">Temperatura del panel negro (°C):</td>
                      <td className="border border-gray-400 p-0.5">
                        <Input className={inputCls('w-full')}
                          value={formData.temp_panel_negro} readOnly={isReadOnly}
                          onChange={e => set('temp_panel_negro', e.target.value)} />
                      </td>
                      <td className="border border-gray-400 px-1 py-0.5">Humedad relativa (%):</td>
                      <td className="border border-gray-400 p-0.5">
                        <Input className={inputCls('w-full')}
                          value={formData.humedad_relativa} readOnly={isReadOnly}
                          onChange={e => set('humedad_relativa', e.target.value)} />
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-400 px-1 py-0.5">Irradiación (W/m²·nm):</td>
                      <td className="border border-gray-400 p-0.5">
                        <Input className={inputCls('w-full')}
                          value={formData.irradiacion} readOnly={isReadOnly}
                          onChange={e => set('irradiacion', e.target.value)} />
                      </td>
                      <td className="border border-gray-400 px-1 py-0.5">Longitud de onda:</td>
                      <td className="border border-gray-400 p-0.5">
                        <Input className={inputCls('w-full')}
                          value={formData.longitud_onda} readOnly={isReadOnly}
                          onChange={e => set('longitud_onda', e.target.value)} />
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* Results inner table */}
                <table className="text-xs border-collapse border border-gray-400" style={{ width: '65%' }}>
                  <tbody>
                    <tr>
                      <td className="border border-gray-400 px-1 py-0.5 w-[60%]">Exposición radiante (kJ/m²):</td>
                      <td className="border border-gray-400 p-0.5">
                        <Input className="h-6 text-xs px-1 border-0 shadow-none bg-gray-50 cursor-not-allowed"
                          value={formData.exposicion_radiante} readOnly />
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-400 px-1 py-0.5">Tiempo de exposición (horas):</td>
                      <td className="border border-gray-400 p-0.5">
                        <Input className={inputCls('w-full')}
                          value={formData.tiempo_exposicion} readOnly={isReadOnly}
                          onChange={e => set('tiempo_exposicion', e.target.value)} />
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-400 px-1 py-0.5">
                        <span className="font-bold">Grado de escala de gris</span><br />
                        <span className="font-bold">(CAMBIO DE COLOR):</span>
                      </td>
                      <td className="border border-gray-400 p-0.5">
                        <Input className={inputCls('w-full')}
                          value={formData.grado_escala_gris} readOnly={isReadOnly}
                          onChange={e => set('grado_escala_gris', e.target.value)} />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>

              {/* Right: Realizó / Autorizó */}
              <td className="border border-gray-500 p-2 align-top w-[22%]">
                <div className="space-y-6">
                  <div>
                    <div className="text-xs font-medium mb-1">Realizó:</div>
                    <Input value={formData.realizo} readOnly
                      className="h-6 text-xs border-0 shadow-none bg-transparent cursor-not-allowed" />
                  </div>
                  <div>
                    <div className="text-xs font-medium mb-1">Autorizó:</div>
                    <Input value={formData.autorizo} readOnly
                      className="h-6 text-xs border-0 shadow-none bg-transparent cursor-not-allowed" />
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── FOOTER ── */}
        <table className="w-full text-xs border-collapse border border-gray-500">
          <tbody>
            <tr>
              <td className="border border-gray-500 px-2 py-1">
                <div className="font-medium mb-1 flex items-center gap-2">
                  Observaciones:
                  <span className="ml-auto flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
                    Mostrar en Reporte Cliente
                    <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                      onCheckedChange={(checked) => set('mostrarObservaciones', checked)} />
                  </span>
                </div>
                <Textarea value={formData.observaciones} readOnly={isReadOnly} rows={2}
                  className={`text-xs ${isReadOnly ? 'border-0 shadow-none bg-transparent resize-none' : ''}`}
                  onChange={e => set('observaciones', e.target.value)} />
              </td>
            </tr>
          </tbody>
        </table>

        {/* Desviación al método */}
        <div>
          <label className="text-xs font-medium block mb-1">Desviación al método</label>
          <Textarea value={formData.desv_metodo} readOnly={isReadOnly} rows={2}
            className={`text-xs ${isReadOnly ? 'bg-muted-foreground/10 cursor-not-allowed' : ''}`}
            onChange={e => set('desv_metodo', e.target.value)} />
        </div>

        <div className="flex gap-2 justify-end">
          {onCancel && <Button variant="outline" onClick={handleBackFlush}>Cancelar</Button>}
          {mode === 'view' && !editMode && !isAceptado && puedeRevisar && (
            <>
              <Button variant="default" onClick={handleAccion}>Aceptar</Button>
              <Button variant="destructive" onClick={() => setShowRechazoModal(true)}>Rechazar</Button>
              <Button variant="secondary" onClick={() => setEditMode(true)}>Editar</Button>
            </>
          )}
          {mode === 'view' && editMode && !isAceptado && puedeRevisar && (
            <Button variant="default" onClick={handleGuardarEdicion}>Guardar Edición</Button>
          )}
          {mode === 'create' && (
            <Button onClick={handleSave} disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar Formulario'}</Button>
          )}
        </div>

      </CardContent>

      <RechazoModal
        open={showRechazoModal}
        onClose={() => setShowRechazoModal(false)}
        loading={rechazoLoading}
        onSubmit={async (razon: string) => {
          setRechazoLoading(true)
          try {
            // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
            // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
            if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
            while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
            await actualizarAceptado(rowId, 'rechazado', razon, undefined, nomId || undefined)
            toast.success('Prueba rechazada')
            setShowRechazoModal(false)
            onRefreshBackend?.()
          } catch (e: any) { toast.error(e.message) }
          finally { setRechazoLoading(false) }
        }}
      />
    </Card>
    </>
  )
}
